import React from "react";
import { useI18n } from "../../i18n";
import { LLC, isLLCFormed } from "../../legal";

export default function Privacy() {
  const { lang } = useI18n();
  const C = COPY[lang];
  return (
    <div className="container-cd py-16 max-w-3xl" data-testid="legal-privacy-page">
      <div className="overline">{C.kicker}</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[0.92]">{C.title}</h1>
      <div className="mt-4 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
        {C.effective}: {LLC.legal_effective_date} · {isLLCFormed ? LLC.name : LLC.pending_label}
      </div>

      <div className="mt-10 space-y-10">
        {C.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-display text-2xl md:text-3xl">{s.h}</h2>
            {(s.p || []).map((para, j) => (
              <p key={j} className="mt-3 text-sm md:text-base" style={{ color: "var(--ink-2)" }}>
                {para}
              </p>
            ))}
            {s.list ? (
              <ul className="mt-3 space-y-2 text-sm md:text-base list-disc pl-6" style={{ color: "var(--ink-2)" }}>
                {s.list.map((li, k) => <li key={k}>{li}</li>)}
              </ul>
            ) : null}
          </section>
        ))}

        <section>
          <h2 className="font-display text-2xl md:text-3xl">{C.contact}</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
            {LLC.name} · {LLC.registered_address_line_1} · {LLC.registered_address_line_2}
            <br />
            {C.privacy_inquiries}: <a className="underline" href={`mailto:${LLC.contact_email}`}>{LLC.contact_email}</a>
          </p>
        </section>
      </div>
    </div>
  );
}

const COPY = {
  en: {
    kicker: "LEGAL · PRIVACY POLICY",
    title: "Privacy Policy",
    effective: "Effective",
    contact: "Contact",
    privacy_inquiries: "Privacy inquiries",
    sections: [
      {
        h: "1. Who we are",
        p: [
          LLC.name + " operates Corridor Digital, a B2B platform connecting IMMEX manufacturers with US buyers. This policy explains what personal information we collect, how we use it, and the choices you have.",
        ],
      },
      {
        h: "2. Information we collect",
        list: [
          "Account information: name, email, password hash (bcrypt), preferred language.",
          "Manufacturer profile information: company name, legal name, RFC, IMMEX registration, certifications, plant address, employee count, contact details, images, documents you upload.",
          "Buyer inquiry information: contact name, email, company, message content, optional phone, optional quantity/timeline.",
          "Payment information: handled by Stripe; we receive billing email, last 4 digits, subscription tier, and Stripe customer ID. We never see full card numbers.",
          "Usage data: pages visited, profile views, share-link opens, IP address (for rate limiting and abuse prevention).",
          "Cookies: a session cookie for authentication and a language preference cookie. Analytics cookies via PostHog (anonymized after 30 days).",
        ],
      },
      {
        h: "3. How we use information",
        list: [
          "To operate the directory, microsites, vault, and inquiry channel.",
          "To verify manufacturer claims (IMMEX, USMCA, certifications) before awarding badges.",
          "To send transactional emails (password reset, payment receipts, RFQ notifications, document expiration alerts).",
          "To send our Corridor Digest newsletter to subscribers who opted in (unsubscribe in one click).",
          "To prevent abuse, rate limit, and detect fraudulent listings.",
          "To translate published profile content via Anthropic Claude API (text only, no PII other than what the profile contains).",
        ],
      },
      {
        h: "4. Sharing",
        p: [
          "We do not sell personal information. We share it only with: Stripe (payments), Anthropic (translation of published profile content), Resend or SendGrid (transactional email if/when configured), MongoDB Atlas (database hosting), and Emergent infrastructure (application hosting). We also share data when required by law (subpoena, court order, regulator request).",
          "Manufacturer profile content that you mark as published is shown publicly on the directory and microsites; this is the intended behavior. Documents in the vault remain private until you create a share link.",
        ],
      },
      {
        h: "5. Retention",
        p: [
          "We retain account and profile data for as long as the account is active. Inquiry messages are retained for 24 months. Cancelled subscription billing data is retained for 7 years (tax compliance). You may request deletion at any time and we will remove personal data within 30 days, subject to legal retention requirements.",
        ],
      },
      {
        h: "6. Your rights",
        p: [
          "Subject to applicable law (US state privacy laws, Mexican Federal Law on Protection of Personal Data, EU GDPR where applicable), you have the right to access, correct, delete, or export your personal data, and to withdraw consent for marketing communications. Email " + LLC.contact_email + " to exercise these rights.",
        ],
      },
      {
        h: "7. Security",
        p: [
          "Passwords are hashed with bcrypt. Authentication is via httpOnly secure cookies. All traffic is served over HTTPS. Document files are stored encrypted at rest in MongoDB. Share links use cryptographically random tokens and can be revoked.",
        ],
      },
      {
        h: "8. International transfers",
        p: [
          "Our hosting and database infrastructure may be located in the United States. By using the Service, you consent to the transfer of your personal data to the United States and any other jurisdiction where our processors operate.",
        ],
      },
      {
        h: "9. Children",
        p: [
          "The Service is intended for B2B users; we do not knowingly collect personal information from anyone under 18.",
        ],
      },
      {
        h: "10. Changes",
        p: [
          "We will post any changes to this policy on this page with the updated effective date. Material changes will be emailed to active accountholders.",
        ],
      },
    ],
  },
  es: {
    kicker: "LEGAL · POLÍTICA DE PRIVACIDAD",
    title: "Política de Privacidad",
    effective: "Vigente desde",
    contact: "Contacto",
    privacy_inquiries: "Consultas de privacidad",
    sections: [
      {
        h: "1. Quiénes somos",
        p: [
          LLC.name + " opera Corridor Digital, una plataforma B2B que conecta a fabricantes IMMEX con compradores en EE.UU. Esta política explica qué información personal recopilamos, cómo la usamos y las opciones que tienes.",
        ],
      },
      {
        h: "2. Información que recopilamos",
        list: [
          "Información de cuenta: nombre, correo, hash de contraseña (bcrypt), idioma preferido.",
          "Información del perfil del fabricante: nombre comercial, razón social, RFC, registro IMMEX, certificaciones, dirección de planta, número de empleados, datos de contacto, imágenes y documentos que subas.",
          "Información de consultas de compradores: nombre, correo, empresa, contenido del mensaje, teléfono opcional, cantidad/plazo opcionales.",
          "Información de pago: manejada por Stripe; recibimos correo de facturación, últimos 4 dígitos, nivel de suscripción e ID de cliente Stripe. Nunca vemos el número completo de la tarjeta.",
          "Datos de uso: páginas visitadas, vistas de perfil, aperturas de enlaces compartidos, dirección IP (para limitación de tasa y prevención de abuso).",
          "Cookies: una cookie de sesión para autenticación y una cookie de preferencia de idioma. Cookies de analítica vía PostHog (anonimizadas a los 30 días).",
        ],
      },
      {
        h: "3. Cómo usamos la información",
        list: [
          "Operar el directorio, micrositios, bóveda y canal de cotizaciones.",
          "Verificar declaraciones de fabricantes (IMMEX, USMCA, certificaciones) antes de otorgar insignias.",
          "Enviar correos transaccionales (reset de contraseña, recibos de pago, notificaciones RFQ, alertas de vencimiento de documentos).",
          "Enviar nuestro boletín Corridor Digest a quienes se suscriban (cancelar en un clic).",
          "Prevenir abuso, limitar tasa de uso y detectar listados fraudulentos.",
          "Traducir contenido publicado del perfil vía API de Anthropic Claude (solo texto, sin datos personales más allá del contenido del perfil).",
        ],
      },
      {
        h: "4. Compartición",
        p: [
          "No vendemos información personal. La compartimos solo con: Stripe (pagos), Anthropic (traducción de contenido publicado), Resend o SendGrid (correo transaccional cuando esté configurado), MongoDB Atlas (hosting de base de datos) e infraestructura Emergent (hosting de la aplicación). También compartimos datos cuando lo exige la ley (orden judicial, requerimiento regulatorio).",
          "El contenido del perfil que marques como publicado se muestra públicamente en el directorio y micrositios; este es el comportamiento previsto. Los documentos en la bóveda permanecen privados hasta que crees un enlace para compartir.",
        ],
      },
      {
        h: "5. Retención",
        p: [
          "Conservamos los datos de cuenta y perfil mientras la cuenta esté activa. Los mensajes de cotización se conservan 24 meses. Los datos de facturación cancelada se conservan 7 años (cumplimiento fiscal). Puedes solicitar la eliminación en cualquier momento y eliminaremos los datos personales en 30 días, sujeto a requisitos legales de retención.",
        ],
      },
      {
        h: "6. Tus derechos",
        p: [
          "Sujeto a la ley aplicable (leyes estatales de privacidad de EE.UU., Ley Federal de Protección de Datos Personales en México, GDPR de la UE cuando aplique), tienes derecho a acceder, corregir, eliminar o exportar tus datos personales, y a retirar el consentimiento para comunicaciones de marketing. Envía un correo a " + LLC.contact_email + " para ejercer estos derechos.",
        ],
      },
      {
        h: "7. Seguridad",
        p: [
          "Las contraseñas se hashean con bcrypt. La autenticación es vía cookies httpOnly seguras. Todo el tráfico se sirve por HTTPS. Los archivos de documentos se almacenan cifrados en reposo en MongoDB. Los enlaces para compartir usan tokens criptográficamente aleatorios y pueden revocarse.",
        ],
      },
      {
        h: "8. Transferencias internacionales",
        p: [
          "Nuestra infraestructura de hosting y base de datos puede estar en Estados Unidos. Al usar el Servicio, consientes la transferencia de tus datos personales a Estados Unidos y a cualquier otra jurisdicción donde operen nuestros procesadores.",
        ],
      },
      {
        h: "9. Menores de edad",
        p: [
          "El Servicio está destinado a usuarios B2B; no recopilamos a sabiendas información personal de menores de 18 años.",
        ],
      },
      {
        h: "10. Cambios",
        p: [
          "Publicaremos cualquier cambio a esta política en esta página con la fecha de vigencia actualizada. Los cambios materiales se enviarán por correo electrónico a los titulares de cuenta activos.",
        ],
      },
    ],
  },
};
